/**
 * Block Properties Manager (BPM) mutations — Phase N.5.
 *
 * Drawing-wide editing of dynamic-block parameter / action metadata. Unlike
 * the BEDIT-gated authoring mutations in `dynamicAuthoringMutations.ts`,
 * these helpers operate on any block by name irrespective of the active
 * space. The Block Properties Manager dialog (BLOCKPROPERTIESMANAGER)
 * needs them because it lets the user audit and tweak every block in the
 * drawing without entering BEDIT for each one.
 *
 * Public surface:
 *   - renameBlockParameterMutation
 *   - updateBlockParameterDescriptionMutation
 *   - toggleBlockParameterShowInPaletteMutation
 *   - reorderBlockParametersMutation
 *   - renameBlockActionMutation
 *   - updateBlockActionDescriptionMutation
 *   - reorderBlockActionsMutation
 *
 * Each mutation:
 *   - throws when the block / parameter / action does not exist;
 *   - re-validates the metadata via `validateDynamicMetadata` so renames
 *     that collide with existing names abort cleanly;
 *   - returns a deterministic summary string (used by the command feed).
 *
 * The mutations share `requireBlock` and `withBlockReplace` from
 * `mutationHelpers.ts`. `ensureMetadata` and `validateAndCommit` are kept
 * private here to avoid coupling with `dynamicAuthoringMutations.ts`
 * internals.
 */

import type {
  BlockDefinition,
  DrawingDocument,
  DynamicBlockMetadata,
} from "@dxf-ai-editor/drawing-model";
import {
  dynamicBlockMetadataSchema,
  validateDynamicMetadata,
} from "@dxf-ai-editor/drawing-model";

import type { MutationResult } from "./dynamicAuthoringMutations";
import { requireBlock, withBlockReplace } from "./mutationHelpers";

// ═══════════════════════════════════════════════════════════════════════
// Helpers
// ═══════════════════════════════════════════════════════════════════════

function ensureMetadata(blockDef: BlockDefinition): DynamicBlockMetadata {
  if (blockDef.dynamicMetadata) return blockDef.dynamicMetadata;
  return dynamicBlockMetadataSchema.parse({});
}

function validateAndCommit(
  document: DrawingDocument,
  blockDef: BlockDefinition,
  nextMeta: DynamicBlockMetadata,
  summary: string,
): MutationResult {
  const parsed = dynamicBlockMetadataSchema.parse(nextMeta);
  const errors = validateDynamicMetadata(parsed);
  if (errors.length > 0) {
    throw new Error(`Invalid dynamic metadata: ${errors.join("; ")}`);
  }
  const nextBlockDef: BlockDefinition = {
    ...blockDef,
    dynamicMetadata: parsed,
  };
  return {
    updatedDocument: withBlockReplace(document, blockDef.name, () => nextBlockDef),
    summary,
  };
}

// ═══════════════════════════════════════════════════════════════════════
// PARAMETER mutations
// ═══════════════════════════════════════════════════════════════════════

export interface RenameBlockParameterInput {
  document: DrawingDocument;
  blockName: string;
  parameterId: string;
  newName: string;
}

/**
 * Rename a parameter by id. Re-validation enforces uniqueness across the
 * block's parameter set; collision throws.
 *
 * Note: actions reference parameters by `parameterId`, not by name, so a
 * rename does NOT require ripple updates. The new name only affects the
 * Properties palette label and the customProperties keys consumed by
 * `applyInstanceParameters` in drawing-model — INSERTs that already store
 * customProperties under the OLD name keep working until the user reseeds
 * them (matches AutoCAD's behaviour after an in-block rename).
 */
export function renameBlockParameterMutation(
  input: RenameBlockParameterInput,
): MutationResult {
  const blockDef = requireBlock(input.document, input.blockName);
  const meta = ensureMetadata(blockDef);
  const existing = meta.parameters.find((p) => p.id === input.parameterId);
  if (!existing) {
    throw new Error(`Parameter '${input.parameterId}' not found`);
  }
  const trimmed = input.newName.trim();
  if (trimmed.length === 0) {
    throw new Error("Parameter name cannot be empty");
  }
  if (trimmed === existing.name) {
    return {
      updatedDocument: input.document,
      summary: `Parameter '${existing.name}' name unchanged`,
    };
  }
  const nextMeta: DynamicBlockMetadata = {
    ...meta,
    parameters: meta.parameters.map((p) =>
      p.id === input.parameterId ? { ...p, name: trimmed } : p,
    ),
  };
  return validateAndCommit(
    input.document,
    blockDef,
    nextMeta,
    `Renamed parameter '${existing.name}' to '${trimmed}' in block '${input.blockName}'`,
  );
}

export interface UpdateBlockParameterDescriptionInput {
  document: DrawingDocument;
  blockName: string;
  parameterId: string;
  description: string;
}

export function updateBlockParameterDescriptionMutation(
  input: UpdateBlockParameterDescriptionInput,
): MutationResult {
  const blockDef = requireBlock(input.document, input.blockName);
  const meta = ensureMetadata(blockDef);
  const existing = meta.parameters.find((p) => p.id === input.parameterId);
  if (!existing) {
    throw new Error(`Parameter '${input.parameterId}' not found`);
  }
  const nextMeta: DynamicBlockMetadata = {
    ...meta,
    parameters: meta.parameters.map((p) =>
      p.id === input.parameterId ? { ...p, description: input.description } : p,
    ),
  };
  return validateAndCommit(
    input.document,
    blockDef,
    nextMeta,
    `Updated description for parameter '${existing.name}' in block '${input.blockName}'`,
  );
}

export interface ToggleBlockParameterShowInPaletteInput {
  document: DrawingDocument;
  blockName: string;
  parameterId: string;
}

/**
 * Flip the parameter's `showInProperties` flag. The current value is read
 * with a fallback to `true` (the AutoCAD default) so legacy fixtures
 * authored before Phase N.5 keep behaving as if the column were enabled.
 */
export function toggleBlockParameterShowInPaletteMutation(
  input: ToggleBlockParameterShowInPaletteInput,
): MutationResult {
  const blockDef = requireBlock(input.document, input.blockName);
  const meta = ensureMetadata(blockDef);
  const existing = meta.parameters.find((p) => p.id === input.parameterId);
  if (!existing) {
    throw new Error(`Parameter '${input.parameterId}' not found`);
  }
  const current = existing.showInProperties ?? true;
  const next = !current;
  const nextMeta: DynamicBlockMetadata = {
    ...meta,
    parameters: meta.parameters.map((p) =>
      p.id === input.parameterId ? { ...p, showInProperties: next } : p,
    ),
  };
  return validateAndCommit(
    input.document,
    blockDef,
    nextMeta,
    `Toggled show-in-palette for parameter '${existing.name}' in block '${input.blockName}' (now ${next ? "shown" : "hidden"})`,
  );
}

export interface ReorderBlockParametersInput {
  document: DrawingDocument;
  blockName: string;
  /** Parameter ids in their NEW order. Must list every parameter exactly once. */
  newOrder: readonly string[];
}

/**
 * Reorder the parameter array. The new ordering must be a permutation of
 * the existing parameter ids — adding / removing parameters is not
 * supported here (use the dedicated add/remove mutations).
 */
export function reorderBlockParametersMutation(
  input: ReorderBlockParametersInput,
): MutationResult {
  const blockDef = requireBlock(input.document, input.blockName);
  const meta = ensureMetadata(blockDef);
  const currentIds = meta.parameters.map((p) => p.id);
  const currentSet = new Set(currentIds);
  const nextSet = new Set(input.newOrder);
  if (
    currentSet.size !== nextSet.size ||
    !currentIds.every((id) => nextSet.has(id))
  ) {
    throw new Error(
      `Reorder must list exactly the existing parameter ids of block '${input.blockName}'`,
    );
  }
  const byId = new Map(meta.parameters.map((p) => [p.id, p] as const));
  const nextParameters = input.newOrder.map((id) => {
    const p = byId.get(id);
    if (!p) throw new Error(`Parameter '${id}' missing during reorder`);
    return p;
  });
  const nextMeta: DynamicBlockMetadata = {
    ...meta,
    parameters: nextParameters,
  };
  return validateAndCommit(
    input.document,
    blockDef,
    nextMeta,
    `Reordered parameters in block '${input.blockName}'`,
  );
}

// ═══════════════════════════════════════════════════════════════════════
// ACTION mutations
// ═══════════════════════════════════════════════════════════════════════

export interface RenameBlockActionInput {
  document: DrawingDocument;
  blockName: string;
  actionId: string;
  newName: string;
}

export function renameBlockActionMutation(
  input: RenameBlockActionInput,
): MutationResult {
  const blockDef = requireBlock(input.document, input.blockName);
  const meta = ensureMetadata(blockDef);
  const existing = meta.actions.find((a) => a.id === input.actionId);
  if (!existing) {
    throw new Error(`Action '${input.actionId}' not found`);
  }
  const trimmed = input.newName.trim();
  if (trimmed.length === 0) {
    throw new Error("Action name cannot be empty");
  }
  const previousLabel = existing.name || existing.id;
  if (trimmed === existing.name) {
    return {
      updatedDocument: input.document,
      summary: `Action '${previousLabel}' name unchanged`,
    };
  }
  const nextMeta: DynamicBlockMetadata = {
    ...meta,
    actions: meta.actions.map((a) =>
      a.id === input.actionId ? { ...a, name: trimmed } : a,
    ),
  };
  return validateAndCommit(
    input.document,
    blockDef,
    nextMeta,
    `Renamed action '${previousLabel}' to '${trimmed}' in block '${input.blockName}'`,
  );
}

export interface UpdateBlockActionDescriptionInput {
  document: DrawingDocument;
  blockName: string;
  actionId: string;
  description: string;
}

export function updateBlockActionDescriptionMutation(
  input: UpdateBlockActionDescriptionInput,
): MutationResult {
  const blockDef = requireBlock(input.document, input.blockName);
  const meta = ensureMetadata(blockDef);
  const existing = meta.actions.find((a) => a.id === input.actionId);
  if (!existing) {
    throw new Error(`Action '${input.actionId}' not found`);
  }
  const nextMeta: DynamicBlockMetadata = {
    ...meta,
    actions: meta.actions.map((a) =>
      a.id === input.actionId ? { ...a, description: input.description } : a,
    ),
  };
  return validateAndCommit(
    input.document,
    blockDef,
    nextMeta,
    `Updated description for action '${existing.name || existing.id}' in block '${input.blockName}'`,
  );
}

export interface ReorderBlockActionsInput {
  document: DrawingDocument;
  blockName: string;
  /** Action ids in their NEW order. Must list every action exactly once. */
  newOrder: readonly string[];
}

export function reorderBlockActionsMutation(
  input: ReorderBlockActionsInput,
): MutationResult {
  const blockDef = requireBlock(input.document, input.blockName);
  const meta = ensureMetadata(blockDef);
  const currentIds = meta.actions.map((a) => a.id);
  const currentSet = new Set(currentIds);
  const nextSet = new Set(input.newOrder);
  if (
    currentSet.size !== nextSet.size ||
    !currentIds.every((id) => nextSet.has(id))
  ) {
    throw new Error(
      `Reorder must list exactly the existing action ids of block '${input.blockName}'`,
    );
  }
  const byId = new Map(meta.actions.map((a) => [a.id, a] as const));
  const nextActions = input.newOrder.map((id) => {
    const a = byId.get(id);
    if (!a) throw new Error(`Action '${id}' missing during reorder`);
    return a;
  });
  const nextMeta: DynamicBlockMetadata = {
    ...meta,
    actions: nextActions,
  };
  return validateAndCommit(
    input.document,
    blockDef,
    nextMeta,
    `Reordered actions in block '${input.blockName}'`,
  );
}
